import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  Filter,
} from "react-bs-datatable";
import { Table, Button, Image, InputGroup, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getWorkspaceAccessById,
  redirectToWorkspaceAccessList,
  workspaceAccessListFetch,
  deleteWorkspaceAccess,
} from "actions";
import DeleteModal from "CommonComponent/DeleteModal";
import VisibleModalContent from "./VisibleModal/VisibleModalContent";
import { getFormatedDateTime, trimAndLimit } from "utils/general";
import { isEmpty } from "lodash";
import AccessRights from "utils/AccessRights";

const headers = [
  { prop: "workspace", title: "Workspace", isSortable: true },
  { prop: "user", title: "User", isSortable: true },
  { prop: "description", title: "Description", isSortable: true },
  { prop: "lastmodified", title: "Last Modified", isSortable: true },
  { prop: "action", title: "Action", isSortable: false },
];

const WorkAccessListing = ({ ...rest }) => {
  const [modalShow, setModalShow] = useState(false);
  const [showViewModel, setShowViewModel] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const session = useSelector((state) => state.session);
  const workspaceAccessState = useSelector(
    (state) => state.workspaceAccessState
  );
  const themeState = useSelector((state) => state.themeState);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [transformedData, setTransformedData] = useState([]);

  // Fetch workspace access list
  useEffect(() => {
    const object_viewed_id = session?.current_page_permissions?.object_id || "";
    dispatch(workspaceAccessListFetch(object_viewed_id));
  }, [dispatch, session]);

  // Store fetched data
  useEffect(() => {
    if (workspaceAccessState.workspaceAccessList) {
      setData(workspaceAccessState.workspaceAccessList);
    }
  }, [workspaceAccessState.workspaceAccessList]);

  // Show view modal
  useEffect(() => {
    setShowViewModel(workspaceAccessState.showViewPage || false);
  }, [workspaceAccessState.showViewPage]);

  // Redirect to edit page if needed
  useEffect(() => {
    if (workspaceAccessState.showUpdateForm) {
      navigate("/workspaceaccess/edit");
    }
  }, [workspaceAccessState.showUpdateForm, navigate]);

  // Transform data for table
  useEffect(() => {
    const flatData = data
      .filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(filterValue.toLowerCase())
        )
      )
      .map((row) => ({
        id: row.id,
        workspace: row.name || "",
        user:
          row.users && row.users.length > 0
            ? row.users.map((u) => u.name).join(", ")
            : "",
        description:
          row.workspace_accesses && row.workspace_accesses.length > 0
            ? row.workspace_accesses[0].description
            : "",
        lastmodified:
          row.workspace_accesses && row.workspace_accesses.length > 0
            ? row.workspace_accesses[0].date_last_modified
            : "",
        original: row, // keep full row for actions
      }));
    setTransformedData(flatData);
  }, [data, filterValue]);

  const getDesc = (val) => {
    let desc = trimAndLimit(val, 200);
    return desc === "null" || desc === null ? "" : desc;
  };

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
  };

  const showViewPopup = (recordId) => {
    dispatch(getWorkspaceAccessById(recordId, "view"));
  };

  const showUpdateForm = (recordId) => {
    dispatch(getWorkspaceAccessById(recordId, "update"));
  };

  const hideModel = () => {
    dispatch(redirectToWorkspaceAccessList());
  };

  const handleDelete = (id) => {
    dispatch(deleteWorkspaceAccess(id));
    setModalShow(false);
  };

  return (
    <>
      <div className="config-table pb-3">
        <DatatableWrapper
          body={transformedData}
          headers={headers}
          paginationOptions={{
            initialState: { rowsPerPage },
            options: [5, 10, 15, 20],
          }}
          onSort={(sortedData) => setTransformedData(sortedData)}
        >
          {/* Search input */}
          <div className="header-input m-auto form-search">
            <InputGroup>
              <InputGroup.Text id="basic-addon1">
                <Image
                  src={
                    themeState.themeMode === "dark"
                      ? "/assets/images/search-icon-dark.svg"
                      : "/assets/images/search-icon.svg"
                  }
                />
              </InputGroup.Text>
              <Form.Control
                value={filterValue}
                onChange={handleFilterChange}
                placeholder="Search..."
                className="font-13 font-weight-normal"
              />
            </InputGroup>
          </div>

          {/* Table */}
          <Table className="p-4 pb-3">
            <TableHeader />
            <TableBody>
              {(tableData) =>
                tableData.map((row) => (
                  <tr key={row.id}>
                    <td className="td-pad ps-3">{row.workspace}</td>
                    <td className="td-pad">{row.user}</td>
                    <td className="td-pad">{getDesc(row.description)}</td>
                    <td className="td-pad">{getFormatedDateTime(row.lastmodified)}</td>
                    <td className="form-action-width">
                      {AccessRights(
                        session.current_page_permissions,
                        "view",
                        row.original.created_by
                      ) && row.original.is_deleted === "n" && (
                        <Button
                          variant="none"
                          className="border-0"
                          onClick={() => showViewPopup(row.id)}
                        >
                          <Image
                            src={
                              themeState.themeMode === "dark"
                                ? "/assets/images/visible-icon-dark.svg"
                                : "/assets/images/visible-icon.svg"
                            }
                          />
                        </Button>
                      )}

                      {AccessRights(
                        session.current_page_permissions,
                        "edit",
                        row.original.created_by
                      ) && row.original.is_deleted === "n" && (
                        <Button
                          className="border-0e"
                          variant="none"
                          onClick={() => showUpdateForm(row.id)}
                        >
                          <Image
                            src={
                              themeState.themeMode === "dark"
                                ? "/assets/images/edit-black-dark.svg"
                                : "/assets/images/edit-black.svg"
                            }
                          />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              }
            </TableBody>
          </Table>
        </DatatableWrapper>
      </div>

      {/* Modals */}
      <DeleteModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        workspace_id={deleteRowId}
      />
      <VisibleModalContent show={showViewModel} onHide={hideModel} />
    </>
  );
};

export default WorkAccessListing;
